import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import PromoCodeRedeem from "./PromoCodeRedeem";

/** Matches PricingPage / UpgradeModal (per-month equivalent when billed annually). */
const TIER_CATALOG = {
  STARTER: {
    name: "Starter",
    monthlyPrice: 19,
    yearlyPrice: 15,
    yearlyTotal: 180,
    tagline: "Social scheduling, AI captions & core publishing",
    features: ["10 posts/day", "3 platforms", "AI captions & video publisher", "Analytics dashboard", "7-day free trial"],
    badge: null,
    color: "#6366f1",
  },
  PRO: {
    name: "Pro",
    monthlyPrice: 39,
    yearlyPrice: 32,
    yearlyTotal: 384,
    tagline: "Deep analytics, Social AI & unlimited posts",
    features: ["Unlimited posts", "6 platforms", "100 AI-generated images/mo", "Deep Trends & Social AI", "Messages & Auto Reply"],
    badge: "Most Popular",
    color: "#8b5cf6",
  },
  GROWTH: {
    name: "Growth",
    monthlyPrice: 79,
    yearlyPrice: 64,
    yearlyTotal: 768,
    tagline: "Priority processing & full platform limits",
    features: ["Unlimited posts", "All platforms", "Unlimited AI-generated images", "Video trimming tool", "Priority processing"],
    badge: "Best Value",
    color: "#0ea5e9",
  },
};

function normalizeTierKey(plan, membershipType) {
  const p = (plan || "").toUpperCase();
  if (p === "STARTER" || p === "PRO" || p === "GROWTH") return p;
  if (p === "MEMBER" || (membershipType || "").toUpperCase() === "MEMBER") return "STARTER";
  const m = (membershipType || "").toUpperCase();
  if (m === "STARTER" || m === "PRO" || m === "GROWTH") return m;
  return null;
}

export default function AccountSettings() {
  const {
    user, token, isSubscribed, cancelSubscription,
    reactivateSubscription, openBillingPortal, deactivateAccount, logout,
    apiBase, authHeaders, refetchUser,
  } = useAuth();

  const [loading, setLoading] = useState(null);
  const [subSnap, setSubSnap] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [deactivatePassword, setDeactivatePassword] = useState("");
  const [confirmCancelSub, setConfirmCancelSub] = useState(false);
  const [message, setMessage] = useState(null);
  const [upgradeYearly, setUpgradeYearly] = useState(false);
  const [useBrandContext, setUseBrandContext] = useState(false);
  const [brandPrefSaving, setBrandPrefSaving] = useState(false);
  // Asset-cron toggle: when ON, the daily AI agent run scans the user's recent
  // unused MediaAssets and drafts ASSET_TO_POST tasks for them (capped at 3/run).
  const [assetCronEnabled, setAssetCronEnabled] = useState(false);
  const [assetCronSaving, setAssetCronSaving]   = useState(false);

  const isMember = isSubscribed;
  const planBadgeLabel = (() => {
    if (!isMember) return "Free";
    const mt = user?.membershipType;
    const map = { STARTER: "Starter", PRO: "Pro", GROWTH: "Growth", MEMBER: "Starter" };
    return map[mt] || mt || "Member";
  })();
  const cancelAtPeriodEnd = user?.cancelAtPeriodEnd === true;
  const periodEnd = user?.subscriptionPeriodEnd;

  useEffect(() => {
    if (!isMember || cancelAtPeriodEnd || !apiBase || !token || user?.id === "owner") {
      setSubSnap(null);
      return undefined;
    }
    let cancelled = false;
    const ac = new AbortController();
    fetch(`${apiBase}/api/subscription/current`, {
      headers: { ...authHeaders(), Accept: "application/json" },
      signal: ac.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled) setSubSnap(d && typeof d === "object" ? d : null);
      })
      .catch(() => {
        if (!cancelled) setSubSnap(null);
      });
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [isMember, cancelAtPeriodEnd, apiBase, token, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the user's "use brand context in all AI" preference once on mount.
  useEffect(() => {
    if (!apiBase || !token) return;
    let cancelled = false;
    fetch(`${apiBase}/api/user/ai-preferences`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        setUseBrandContext(!!d.useBrandContext);
        setAssetCronEnabled(!!d.assetCronEnabled);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [apiBase, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleBrandContext = async (next) => {
    if (brandPrefSaving) return;
    setBrandPrefSaving(true);
    setUseBrandContext(next); // optimistic
    try {
      const r = await fetch(`${apiBase}/api/user/ai-preferences`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ useBrandContext: next }),
      });
      if (!r.ok) throw new Error("save failed");
      setMessage({ text: next
        ? "AI agents will now use your brand profile."
        : "AI agents will now use generic prompts (no brand context).", error: false });
    } catch (e) {
      setUseBrandContext(!next); // revert on failure
      setMessage({ text: "Could not save preference. Try again.", error: true });
    } finally {
      setBrandPrefSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleToggleAssetCron = async (next) => {
    if (assetCronSaving) return;
    setAssetCronSaving(true);
    setAssetCronEnabled(next); // optimistic
    try {
      const r = await fetch(`${apiBase}/api/user/ai-preferences`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ assetCronEnabled: next }),
      });
      if (!r.ok) throw new Error("save failed");
      setMessage({ text: next
        ? "Daily asset cron is ON. AI will draft posts from new assets."
        : "Daily asset cron is OFF. Manual generation still works.", error: false });
    } catch (e) {
      setAssetCronEnabled(!next); // revert
      setMessage({ text: "Could not save preference. Try again.", error: true });
    } finally {
      setAssetCronSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const tierKey = normalizeTierKey(subSnap?.plan, user?.membershipType);

  /** Subscription pill — matches pricing card colors (solid fill + white text). */
  const subscriptionBadgeStyle = (() => {
    if (!isMember) return s.freeBadge;
    const key = tierKey || (user?.membershipType === "MEMBER" ? "STARTER" : null);
    const tier = key && TIER_CATALOG[key] ? TIER_CATALOG[key] : null;
    if (!tier) {
      return { ...s.memberBadge, background: "#6366f1", color: "#fff", border: "1px solid #6366f1" };
    }
    return {
      padding: "3px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "700",
      background: tier.color,
      color: "#ffffff",
      border: `1px solid ${tier.color}`,
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
    };
  })();
  const showMessage = (msg, isError = false) => {
    setMessage({ text: msg, error: isError });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleCancelSub = async () => {
    if (!confirmCancelSub) { setConfirmCancelSub(true); return; }
    setLoading("cancel-sub");
    try {
      await cancelSubscription();
      setConfirmCancelSub(false);
      showMessage("Subscription will cancel at the end of the current period.");
    } catch (e) { showMessage(e.message || "Could not cancel subscription", true); }
    finally { setLoading(null); }
  };

  const handleReactivateSub = async () => {
    setLoading("reactivate-sub");
    try {
      await reactivateSubscription();
      showMessage("Subscription reactivated! Your membership will continue.");
    } catch (e) { showMessage(e.message || "Could not reactivate subscription", true); }
    finally { setLoading(null); }
  };

  const handleBilling = async () => {
    setLoading("billing");
    try { await openBillingPortal(); }
    catch (e) { showMessage(e.message || "Failed to open billing portal.", true); }
    finally { setLoading(null); }
  };

  const handleDeactivate = async () => {
    if (!confirmDeactivate) { setConfirmDeactivate(true); return; }
    setLoading("deactivate");
    try {
      await deactivateAccount(deactivatePassword);
      logout();
    } catch (e) { showMessage(e.message || "Deactivation failed", true); }
    finally { setLoading(null); }
  };

  const openChangePlan = () => {
    window.dispatchEvent(new CustomEvent("wintaibot:go", { detail: "pricing" }));
  };

  const handleUpgradeTo = async (planId) => {
    if (!apiBase) return;
    setLoading(`upgrade-${planId}`);
    try {
      const res = await fetch(`${apiBase}/api/subscription/checkout`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ plan: planId, billingInterval: upgradeYearly ? "YEARLY" : "MONTHLY" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || "Checkout failed");
      if (data.updated) {
        showMessage(data.message || "Plan updated!", false);
        if (typeof refetchUser === "function") await refetchUser();
        return;
      }
      const url = data.url || data.checkoutUrl;
      if (url) window.location.href = url;
      else throw new Error("Checkout failed");
    } catch (e) {
      showMessage(e.message || "Could not start checkout", true);
    } finally {
      setLoading(null);
    }
  };

  // Plans to show in upgrade section
  const upgradePlans = !isMember
    ? ["STARTER", "PRO", "GROWTH"]
    : tierKey === "STARTER"
    ? ["PRO", "GROWTH"]
    : null; // PRO/GROWTH users don't see upgrade section

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <h2 style={s.cardTitle}>Account Settings</h2>
        <p style={s.cardEmail}>{user?.email}</p>

        {/* Toast */}
        {message && (
          <div style={{
            ...s.toast,
            background: message.error ? "#fef2f2" : "#f0fdf4",
            color: message.error ? "#b91c1c" : "#15803d",
            borderColor: message.error ? "#fecaca" : "#bbf7d0",
          }}>
            {message.error ? "⚠ " : "✓ "}{message.text}
          </div>
        )}

        {/* Subscription */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Subscription</h3>
          <div style={s.badgeRow}>
            <span style={isMember ? subscriptionBadgeStyle : s.freeBadge}>
              {planBadgeLabel}
            </span>
            {isMember && cancelAtPeriodEnd && (
              <span style={s.warnBadge}>Cancels {periodEnd ? `on ${periodEnd}` : "at period end"}</span>
            )}
            {isMember && !cancelAtPeriodEnd && periodEnd && (
              <span style={s.renewText}>Renews {periodEnd}</span>
            )}
          </div>

          {isMember && !cancelAtPeriodEnd && user?.id !== "owner" && (
            <div style={{ marginTop: 14 }}>
              <p style={s.desc}>
                Switch to another tier or change monthly vs yearly billing. You can also use Stripe for payment method and invoices.
              </p>
              <button
                type="button"
                onClick={openChangePlan}
                disabled={!!loading}
                style={s.btnPrimary}
              >
                Change plan or billing
              </button>
            </div>
          )}

          {/* ── Inline upgrade section: Free → all plans, Starter → Pro/Growth ── */}
          {upgradePlans && (
            <div style={{ marginTop: 20 }}>
              <p style={{ ...s.desc, marginBottom: 14 }}>
                {!isMember
                  ? "Choose a plan to unlock all features."
                  : "Unlock more with Pro or Growth."}
              </p>

              {/* Monthly / Yearly toggle */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 18 }}>
                <div style={{ display: "flex", border: "1.5px solid #e2e8f0", borderRadius: 10, overflow: "hidden", background: "#f8fafc" }}>
                  <button
                    type="button"
                    onClick={() => setUpgradeYearly(false)}
                    style={{
                      padding: "7px 16px", border: "none", fontSize: 13, fontWeight: 600,
                      cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                      background: !upgradeYearly ? "#fff" : "transparent",
                      color: !upgradeYearly ? "#1e293b" : "#64748b",
                      boxShadow: !upgradeYearly ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                    }}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setUpgradeYearly(true)}
                    style={{
                      padding: "7px 16px", border: "none", fontSize: 13, fontWeight: 600,
                      cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                      background: upgradeYearly ? "#fff" : "transparent",
                      color: upgradeYearly ? "#6366f1" : "#64748b",
                      boxShadow: upgradeYearly ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                      display: "flex", alignItems: "center", gap: 6,
                    }}
                  >
                    Yearly
                    <span style={{
                      background: "#dcfce7", color: "#15803d",
                      fontSize: 10, fontWeight: 700, padding: "2px 6px",
                      borderRadius: 20, whiteSpace: "nowrap",
                    }}>
                      Save ~18%
                    </span>
                  </button>
                </div>
              </div>

              {/* Plan cards */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {upgradePlans.map((planKey) => {
                  const plan = TIER_CATALOG[planKey];
                  const isRecommended = planKey === "PRO";
                  const price = upgradeYearly ? plan.yearlyPrice : plan.monthlyPrice;
                  const savingsYr = plan.monthlyPrice * 12 - plan.yearlyTotal;
                  const isLoadingThis = loading === `upgrade-${planKey}`;

                  return (
                    <div
                      key={planKey}
                      style={{
                        flex: "1 1 200px", minWidth: 180,
                        border: `2px solid ${isRecommended ? plan.color : "#e2e8f0"}`,
                        borderRadius: 14, padding: "18px 16px",
                        background: isRecommended ? `${plan.color}08` : "#fff",
                        position: "relative", display: "flex", flexDirection: "column",
                        boxShadow: isRecommended ? `0 4px 16px ${plan.color}22` : "none",
                      }}
                    >
                      {/* Badge */}
                      {plan.badge && (
                        <span style={{
                          position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)",
                          background: plan.color, color: "#fff",
                          fontSize: 10, fontWeight: 800, padding: "3px 10px",
                          borderRadius: 20, whiteSpace: "nowrap", letterSpacing: "0.03em",
                        }}>
                          {plan.badge}
                        </span>
                      )}

                      {/* Plan name */}
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>
                        {plan.name}
                      </div>

                      {/* Price */}
                      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, marginBottom: 4 }}>
                        <span style={{ fontSize: 32, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>${price}</span>
                        <span style={{ fontSize: 13, color: "#64748b", paddingBottom: 3 }}>/mo</span>
                      </div>

                      {/* Billing note */}
                      <div style={{ fontSize: 11, color: upgradeYearly ? "#15803d" : "#94a3b8", marginBottom: 14, fontWeight: upgradeYearly ? 600 : 400 }}>
                        {upgradeYearly
                          ? `Billed $${plan.yearlyTotal}/yr · save $${savingsYr}/yr`
                          : "Billed monthly"}
                      </div>

                      {/* Features */}
                      <ul style={{ margin: "0 0 16px", padding: 0, listStyle: "none", flex: 1 }}>
                        {plan.features.map((f, i) => (
                          <li key={i} style={{ fontSize: 12, color: "#475569", marginBottom: 5, display: "flex", gap: 6 }}>
                            <span style={{ color: plan.color, fontWeight: 700, flexShrink: 0 }}>✓</span>
                            {f}
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <button
                        type="button"
                        onClick={() => handleUpgradeTo(planKey)}
                        disabled={!!loading}
                        style={{
                          width: "100%", padding: "10px 0", borderRadius: 9,
                          border: "none", cursor: !!loading ? "not-allowed" : "pointer",
                          background: isRecommended ? plan.color : "#0f172a",
                          color: "#fff", fontSize: 13, fontWeight: 700,
                          fontFamily: "inherit", opacity: !!loading ? 0.7 : 1,
                          transition: "opacity 0.15s",
                        }}
                      >
                        {isLoadingThis
                          ? "Redirecting…"
                          : planKey === "STARTER"
                          ? "Start Free Trial"
                          : `Upgrade to ${plan.name}`}
                      </button>
                    </div>
                  );
                })}
              </div>

              {upgradeYearly && (
                <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>
                  Annual plans billed as one payment · Cancel anytime · Powered by Stripe
                </p>
              )}
              {!upgradeYearly && upgradePlans.includes("STARTER") && (
                <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>
                  Starter includes 7-day free trial · No charge until trial ends
                </p>
              )}
            </div>
          )}

          {isMember && !cancelAtPeriodEnd && (
            <div style={{ marginTop: 12 }}>
              {!confirmCancelSub ? (
                <button onClick={() => setConfirmCancelSub(true)} disabled={!!loading} style={s.btnWarning}>
                  Cancel Subscription
                </button>
              ) : (
                <div style={s.confirmBox}>
                  <p style={s.warnMsg}>Your subscription will cancel at period end{periodEnd ? ` (${periodEnd})` : ""}. You keep access until then.</p>
                  <div style={s.btnRow}>
                    <button onClick={() => setConfirmCancelSub(false)} style={s.btnGhost}>Keep Subscription</button>
                    <button onClick={handleCancelSub} disabled={loading === "cancel-sub"} style={s.btnWarning}>
                      {loading === "cancel-sub" ? "Cancelling..." : "Confirm Cancel"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {isMember && cancelAtPeriodEnd && (
            <div style={{ marginTop: 12 }}>
              <p style={s.desc}>Your subscription ends soon. Reactivate to keep your membership.</p>
              <button onClick={handleReactivateSub} disabled={loading === "reactivate-sub"} style={s.btnSuccess}>
                {loading === "reactivate-sub" ? "Reactivating..." : "Reactivate Subscription"}
              </button>
            </div>
          )}
        </div>

        {/* AI Preferences */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>AI Preferences</h3>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>
                Use my brand profile in all AI generation
              </div>
              <p style={{ ...s.desc, marginTop: 0 }}>
                When ON, agents (Writer, Strategist, Auto-Reply, Ask AI, Document Q&amp;A, etc.) prepend
                your active brand voice, audience, and key phrases to every prompt. When OFF (default),
                they use generic prompts. Brand-specific endpoints (Hooks, Video Captions, Voice → Content)
                always use the brand regardless of this toggle.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={useBrandContext}
              disabled={brandPrefSaving}
              onClick={() => handleToggleBrandContext(!useBrandContext)}
              style={{
                flexShrink: 0,
                width: 52, height: 28, borderRadius: 999,
                border: "none", cursor: brandPrefSaving ? "wait" : "pointer",
                background: useBrandContext ? "#6366f1" : "#cbd5e1",
                position: "relative", transition: "background 0.2s",
                opacity: brandPrefSaving ? 0.6 : 1,
              }}
              title={useBrandContext ? "Click to turn OFF" : "Click to turn ON"}
            >
              <span style={{
                position: "absolute", top: 3, left: useBrandContext ? 27 : 3,
                width: 22, height: 22, borderRadius: "50%",
                background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }} />
            </button>
          </div>

          {/* Asset auto-suggest cron */}
          <div style={{
            display: "flex", alignItems: "flex-start", justifyContent: "space-between",
            gap: 16, marginTop: 18, paddingTop: 18, borderTop: "1px solid #e2e8f0",
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>
                Auto-suggest posts from new assets (daily)
              </div>
              <p style={{ ...s.desc, marginTop: 0 }}>
                When ON, the daily AI agent run scans the latest assets in your Asset Library
                and creates platform-tailored draft posts for the first 3 unused assets — ready for
                your review in AI Workspace → Pending Tasks. OFF by default. Manual generation
                from any asset always works regardless of this setting.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={assetCronEnabled}
              disabled={assetCronSaving}
              onClick={() => handleToggleAssetCron(!assetCronEnabled)}
              style={{
                flexShrink: 0,
                width: 52, height: 28, borderRadius: 999,
                border: "none", cursor: assetCronSaving ? "wait" : "pointer",
                background: assetCronEnabled ? "#6366f1" : "#cbd5e1",
                position: "relative", transition: "background 0.2s",
                opacity: assetCronSaving ? 0.6 : 1,
              }}
              title={assetCronEnabled ? "Click to turn OFF" : "Click to turn ON"}
            >
              <span style={{
                position: "absolute", top: 3, left: assetCronEnabled ? 27 : 3,
                width: 22, height: 22, borderRadius: "50%",
                background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }} />
            </button>
          </div>
        </div>

        {/* Billing */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Billing &amp; Invoices</h3>
          <p style={s.desc}>
            View invoice history, update payment method, or manage your subscription in Stripe&apos;s billing portal.
            W!ntAi does not store your bank account or full card details; only Stripe processes that information.
          </p>
          <button onClick={handleBilling} disabled={!!loading} style={s.btnPrimary}>
            {loading === "billing" ? "Opening..." : "Manage Billing & Invoices"}
          </button>
        </div>

        {/* Promo / Beta Code */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>🎟 Promo Code</h3>
          {user?.freeUntil ? (
            // User already has a redeemed code granting free access — show a status badge
            // instead of the redeem input. freeUntil is null = lifetime; specific date = expires.
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "10px 14px", borderRadius: 10,
              background: "#f0fdf4", border: "1px solid #bbf7d0",
              color: "#15803d", fontSize: 14, fontWeight: 600,
            }}>
              <span>🎁</span>
              <span>
                Free access via promo code
                {(() => {
                  // freeUntil is an ISO timestamp. If it's >50 years in the future treat as lifetime.
                  try {
                    const until = new Date(user.freeUntil);
                    const yearsAway = (until.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 365);
                    if (yearsAway > 50) return " — lifetime";
                    return ` — until ${until.toLocaleDateString()}`;
                  } catch { return ""; }
                })()}
              </span>
            </div>
          ) : (
            <>
              <p style={s.desc}>
                Have a code from W!ntAi? Enter it here to unlock free access or a discount.
                FREE codes activate instantly; percentage-discount codes apply at checkout.
              </p>
              <PromoCodeRedeem onRedeemed={() => {
                // After a FREE code is redeemed, the user's freeUntil flips on. Refresh
                // the user object so the badge above appears without a manual page reload.
                if (typeof refetchUser === "function") refetchUser();
              }} />
            </>
          )}
        </div>

        {/* Deactivate */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Deactivate Account</h3>
          <p style={s.desc}>Deactivate your account. You can reactivate it anytime by logging in with your credentials.</p>
          {!confirmDeactivate ? (
            <button onClick={() => setConfirmDeactivate(true)} style={s.btnDanger}>
              Deactivate Account
            </button>
          ) : (
            <div style={s.confirmBox}>
              <p style={s.warnMsg}>Enter your password to confirm account deactivation. You can reactivate later by signing in.</p>
              <input
                type="password"
                placeholder="Your password"
                value={deactivatePassword}
                onChange={(e) => setDeactivatePassword(e.target.value)}
                style={s.input}
              />
              <div style={s.btnRow}>
                <button onClick={() => { setConfirmDeactivate(false); setDeactivatePassword(""); }} style={s.btnGhost}>Cancel</button>
                <button onClick={handleDeactivate} disabled={loading === "deactivate" || !deactivatePassword} style={s.btnDanger}>
                  {loading === "deactivate" ? "Deactivating..." : "Confirm Deactivate"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  wrap: {
    padding: "4px 0",
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  card: {
    background: "#ffffff",
    borderRadius: "12px",
    // Longhand instead of shorthand `border` so child styles that imperatively
    // change `borderColor`/`borderTop` don't trigger React's shorthand-vs-longhand warning.
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#e2e8f0",
    padding: "32px 40px",
    // Wider card so descriptions don't wrap into 4-5 lines and the page feels
    // less like a long scrolling column. Caps at 1100px so it stays readable
    // on ultrawide monitors.
    maxWidth: "1100px",
    width: "100%",
    margin: "0 auto",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  cardTitle: { margin: "0 0 6px", color: "#0f172a", fontSize: "20px", fontWeight: "700" },
  cardEmail: { margin: "0 0 20px", color: "#64748b", fontSize: "14px" },
  toast: {
    padding: "10px 14px", borderRadius: "8px",
    // Longhand so the inline `borderColor` set by the toast renderer doesn't
    // collide with the `border` shorthand on the same element.
    borderWidth: "1px", borderStyle: "solid",
    marginBottom: "16px", fontSize: "14px", fontWeight: "500",
  },
  // Tightened from 20/20 → 14/14 since the wider card (1100px) means each
  // section's content now wraps to fewer lines, so the previous spacing felt
  // overly stretched.
  section: { borderTop: "1px solid #f1f5f9", paddingTop: "14px", marginTop: "14px" },
  sectionTitle: { margin: "0 0 10px", color: "#0f172a", fontSize: "15px", fontWeight: "700" },
  desc: { margin: "0 0 14px", color: "#64748b", fontSize: "14px", lineHeight: "1.6" },
  badgeRow: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" },
  memberBadge: {
    padding: "3px 12px", borderRadius: "20px",
    background: "#6366f1", color: "#fff", border: "1px solid #6366f1",
    fontSize: "12px", fontWeight: "700",
  },
  freeBadge: {
    padding: "3px 12px", borderRadius: "20px",
    background: "#94a3b8", color: "#fff",
    fontSize: "12px", fontWeight: "700",
  },
  warnBadge: {
    padding: "3px 12px", borderRadius: "20px",
    background: "#fbbf24", color: "#1c1917",
    fontSize: "12px", fontWeight: "600",
  },
  renewText: { color: "#64748b", fontSize: "13px" },
  confirmBox: { paddingTop: "4px" },
  warnMsg: { color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "10px 12px", fontSize: "13px", lineHeight: "1.5", marginBottom: "12px" },
  btnRow: { display: "flex", gap: "10px", flexWrap: "wrap" },
  input: {
    width: "100%", padding: "10px 12px", borderRadius: "8px",
    border: "1px solid #e2e8f0", fontSize: "14px",
    marginBottom: "12px", boxSizing: "border-box", outline: "none",
    fontFamily: "inherit",
  },
  btnPrimary: {
    padding: "10px 20px", borderRadius: "8px", border: "none",
    background: "#2563eb", color: "#fff",
    fontSize: "14px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit",
  },
  btnSuccess: {
    padding: "10px 20px", borderRadius: "8px", border: "none",
    background: "#22c55e", color: "#fff",
    fontSize: "14px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit",
  },
  btnWarning: {
    padding: "10px 20px", borderRadius: "8px", border: "none",
    background: "#f97316", color: "#fff",
    fontSize: "14px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit",
  },
  btnDanger: {
    padding: "10px 20px", borderRadius: "8px", border: "none",
    background: "#ef4444", color: "#fff",
    fontSize: "14px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit",
  },
  btnGhost: {
    padding: "10px 18px", borderRadius: "8px",
    border: "1px solid #e2e8f0", background: "#fff",
    color: "#475057", fontSize: "14px", fontWeight: "600",
    cursor: "pointer", fontFamily: "inherit",
  },
};
